import App from './application-container';
import { animate } from './scene';
import HashTable from './hashtable';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as THREE from 'three';
import { show_modal, hide_modal } from './modals';
import * as vector3 from './vector3';
import * as maths from './maths';
import { WrappedValue } from '@phi/wrapped-value';
import { sumBy } from '@vaaas/fpts/iter';
import { next_frame } from '@vaaas/fpts/promise';
import { withProgress, withSpinner } from './state/spinner';
import { destroyClickedElement } from './input_output';
import { uid_calculate_bounding_box_of_selected_objects, uid_calculate_global_bounding_box } from './ui_display';
import {
    get_number,
  push_nodups,
    raycaster_intersect_objects,
    run_consistency_checks,
    set_smoothen_strength,
    suppress_animate,
    suppress_undo_tracking,
    unsuppress_animate_if_ready,
    update_dom_text_values_and_coordinates,
} from './utility';
import { FAINT_STRENGTH } from './vars_and_data_structures';
import BBInfo from './BBInfo';
import { plane_ray_intersect } from './geometry_and_math';
import { uil_get_face_geometry, uil_get_point_at_edge_param, uil_is_object_visible, uil_move_vertex_to_coords, uil_unfix_objects } from './ui_logic';
import { Uid_face_triangle, Uid_ref_face, Uid_ref_face_triangle } from './graph_objects';
import { clipping_on, clipping_planes } from '../../../../src/features/clipping';

export function start_attaching_to_stl() {
  // Empty the hash
  vertices_to_attach_move_directions = new HashTable();

  // Check that user has selected something
  var vertices_to_attach = [];
  uid_selected_vertices.keys().forEach(function(vid){
    vertices_to_attach.push(vid);
  });

  uid_selected_edges.keys().forEach(function(eid){
    var vs = uil_get_vertices_of_edge(eid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
  });

  uid_selected_faces.keys().forEach(function(fid){
    var vs = vertices_of_face(fid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
  });

  if (vertices_to_attach.length == 0) {
    display_message_to_user(LOG.WARN, "You haven't selected anything, aborting.");
    return;
  }


  // Remove if there, and add back.
  scene.remove(uid_ref_faces_for_attaching_surface_raynode);
  scene.add(uid_ref_faces_for_attaching_surface_raynode);

  // Calculate a bounding box for the Phi objects that will be attached to the surface
  var phi_bbox = uid_calculate_bounding_box_of_selected_objects(true);
  var phi_bbox_center = new THREE.Vector3(phi_bbox.centerx, phi_bbox.centery, phi_bbox.centerz);

  // Remove all reference faces except the one we'll be attaching to
  // The special face to attach will go under a new raynode: uid_ref_faces_for_attaching_surface_raynode
  // Hide the standard STL reference faces raynode (uid_ref_stl_faces_raynode)
  if (uid_reference_faces.length == 1)
    reference_object_for_attaching_surface = uid_reference_faces.firstValue();
  else {
    var closest_dist = 9999;
    var closest_index = 0;
    for (var i = 0; i < uid_reference_faces.length; i++) {
      var ref_face = uid_reference_faces.getItemAtIndex(i);
      var ref_bbox = uid_calculate_point_cloud_bounding_box(ref_face.triangle_vertices);
      var ref_bbox_center = new THREE.Vector3(ref_bbox.centerx, ref_bbox.centery, ref_bbox.centerz);
      if (vector3.point_point_distance(phi_bbox_center, ref_bbox_center) < closest_dist) {
        closest_dist = vector3.point_point_distance(phi_bbox_center, ref_bbox_center);
        closest_index = i;
      }
    }
    reference_object_for_attaching_surface = uid_reference_faces.getItemAtIndex(closest_index);
  }

  uid_reference_stl_raynode.remove(uid_ref_stl_faces_raynode);

  // Remove part of STL that is distant from Phi objects
  all_ref_points = [... reference_object_for_attaching_surface.triangle_vertices];
  all_ref_triangles = [... reference_object_for_attaching_surface.triangles];
  remove_irrelevant_part_of_STL(phi_bbox, 0.5);

  // Find the bounding boxes of the reference points and the selected phi points that will be attached
  var ref_bbox = uid_calculate_point_cloud_bounding_box(all_ref_points);
  var phi_vertex_density = uid_vertices.length / (phi_bbox.dimension * phi_bbox.dimension);
  console.log("Reference bbox dimension: " + ref_bbox.dimension);
  console.log("Phi bbox dimension: " + phi_bbox.dimension);
  console.log("Reference number of vertices: " + all_ref_points.length);
  console.log("Phi vertices: " + uid_vertices.length);
  console.log("Phi vertex density: " + phi_vertex_density);

  // If there is need, resample reference to add more vertices. To resample it we must add one new vertex in the middle of each triangle,
  // and form a new triangle there.
  var done_enriching = true;
  while (!done_enriching) {
    var ref_vertex_density = all_ref_points.length / (ref_bbox.dimension * ref_bbox.dimension);
    console.log("Reference vertex density: " + ref_vertex_density);
    if (ref_vertex_density > 2 * phi_vertex_density)
      done_enriching = true;
    else
      enrich_reference_vertices(all_ref_points, all_ref_triangles);
  }
  console.log("Final Reference vertex density: " + ref_vertex_density);

  // Fill up the STL triangles with some useful information.
  // - vector1 will store the triangle normal
  // - vector2 will store the triangle barycenter
  // - vector3 will store the distance between the barycenter and the most distant vertex, so that a disk from it will
  //   encompass the entire triangle. NOTE: This is not a vector any more, it's a scalar. Naming sucks but I don't want to screw up
  //   things too much right now.
  all_ref_triangles.forEach(function(rtr){
    fill_up_STL_triangle_data(rtr);
  });

  // Create slot hash for all reference points
  stl_triplets_hash = new Coordinate_triplets_hash(ref_bbox, 10);
  all_ref_triangles.forEach(function(tr) {
    stl_triplets_hash.add_element_to_slot_hash_for_point(all_ref_points[tr.index1], tr);
    stl_triplets_hash.add_element_to_slot_hash_for_point(all_ref_points[tr.index2], tr);
    stl_triplets_hash.add_element_to_slot_hash_for_point(all_ref_points[tr.index3], tr);
  });

  App.state.attaching_to_stl = true;
  set_button_states();
}

export function attach_surface_to_reference_model_stage1() {
  display_message_to_user(LOG.INFO, "Attaching to reference STL model: Attach vertices");

  clear_small_balls();
  App.state.always_run_consistency_checks_on_vertices = false;
  App.state.always_run_consistency_checks_on_rest = false;

  var vertices_to_attach = [];
  var edges_to_attach = [];

  uid_selected_vertices.keys().forEach(function(vid){
    vertices_to_attach.push(vid);
  });

  uid_selected_edges.keys().forEach(function(eid){
    edges_to_attach.push(eid);
    var vs = uil_get_vertices_of_edge(eid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
  });

  uid_selected_faces.keys().forEach(function(fid){
    var vs = vertices_of_face(fid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
    var es = uil_get_edges_of_face(fid);
    es.forEach(function(eid){
      push_nodups(eid, edges_to_attach);
    });
  });

  if (!all_ref_points) {
    display_message_to_user(LOG.WARN, "No reference points, aborting.");
    return;
  }
  if (vertices_to_attach.length == 0) {
    display_message_to_user(LOG.WARN, "You haven't selected anything, aborting.");
    return;
  }

  var ref_bbox = uid_calculate_point_cloud_bounding_box(all_ref_points);
  var phi_bbox = uid_calculate_bounding_box_of_selected_objects(true);

  attach_vertices_to_reference_model(ref_bbox, phi_bbox, all_ref_points, vertices_to_attach, edges_to_attach, vertices_to_attach_move_directions);
}

export function attach_surface_to_reference_model_stage2(type) {
  display_message_to_user(LOG.INFO, "Attaching to reference STL model: Attach edges");

  clear_small_balls();
  App.state.always_run_consistency_checks_on_vertices = false;
  App.state.always_run_consistency_checks_on_rest = false;

  var vertices_to_attach = [];
  var edges_to_attach = [];

  uid_selected_vertices.keys().forEach(function(vid){
    vertices_to_attach.push(vid);
  });

  uid_selected_edges.keys().forEach(function(eid){
    edges_to_attach.push(eid);
    var vs = uil_get_vertices_of_edge(eid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
  });

  uid_selected_faces.keys().forEach(function(fid){
    var vs = vertices_of_face(fid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
    var es = uil_get_edges_of_face(fid);
    es.forEach(function(eid){
      push_nodups(eid, edges_to_attach);
    });
  });


  // Check that we already information in  vertices_to_attach_move_directions for all of these vertices
  var missing_info = false;
  edges_to_attach.forEach(function(eid){
    var vs = uil_get_vertices_of_edge(eid);
    if (!vertices_to_attach_move_directions.hasItem(vs[0]))
      missing_info = true;
    if (!vertices_to_attach_move_directions.hasItem(vs[1]))
      missing_info = true;
  });
  if (missing_info) {
    display_message_to_user(LOG.WARN, "You have not run the first stage for some of the vertices involved. Please run the first stage and try again.");
    return;
  }

  var ref_bbox = uid_calculate_point_cloud_bounding_box(all_ref_points);
  var phi_bbox = uid_calculate_global_bounding_box(true, true, false);

//  remove_irrelevant_part_of_STL(phi_bbox, 0.15);

  all_ref_triangles.forEach(function(rtr){
    fill_up_STL_triangle_data(rtr);
  });

  // Re-create slot hash for all reference points
  var stl_triplets_hash = new Coordinate_triplets_hash(ref_bbox, 10);
  all_ref_triangles.forEach(function(tr) {
    stl_triplets_hash.add_element_to_slot_hash_for_point(all_ref_points[tr.index1], tr);
    stl_triplets_hash.add_element_to_slot_hash_for_point(all_ref_points[tr.index2], tr);
    stl_triplets_hash.add_element_to_slot_hash_for_point(all_ref_points[tr.index3], tr);
  });

  var ref_bbox = uid_calculate_point_cloud_bounding_box(all_ref_points);
  var res2 = attach_edge_samples_to_reference_model(ref_bbox, all_ref_points, edges_to_attach, vertices_to_attach_move_directions, phi_bbox, type);
}

export const attach_surface_to_reference_model_stage3 = withSpinner('Attaching to reference STL model: Smooth and adjust', async function attach_surface_to_reference_model_stage3() {
  clear_small_balls();
  App.state.always_run_consistency_checks_on_vertices = false;
  App.state.always_run_consistency_checks_on_rest = false;
  set_smoothen_strength(FAINT_STRENGTH);
  await next_frame();
  uil_fix_objects(uid_vertices.keys());
  await next_frame();
  uil_smoothen_edges(uid_selected_edges.keys());
  await next_frame();
  run_consistency_checks(true, false);
  await next_frame();
  uil_unfix_objects(uid_vertices.keys());
});

export function stop_attaching_to_stl() {
  clear_small_balls();
  uid_reference_stl_raynode.remove(uid_ref_faces_for_attaching_surface_raynode);
  uid_reference_stl_raynode.add(uid_ref_stl_faces_raynode);
  reset_original_STL();
  App.state.attaching_to_stl = false;
  set_button_states();
}

export function uid_calculate_point_cloud_bounding_box(points) {
  if (points.length == 0) {
    // No reference objects points, return default
    var bb = new BBInfo( (-1) * viewSize / 5, viewSize / 5, (-1) * viewSize / 5, viewSize / 5, (-1) * viewSize / 5, viewSize / 5,);
    return bb;
  }

  // Then we calculate a global bounding box.
  var minx = 10000000;
  var miny = 10000000;
  var minz = 10000000;
  var maxx = -10000000;
  var maxy = -10000000;
  var maxz = -10000000;

  points.forEach(function(v) {
      if (v.x < minx)
        minx = v.x;
      if (v.x > maxx)
        maxx = v.x;
      if (v.y < miny)
        miny = v.y;
      if (v.y > maxy)
        maxy = v.y;
      if (v.z < minz)
        minz = v.z;
      if (v.z > maxz)
        maxz = v.z;
  });
  var bb = new BBInfo(minx, maxx, miny, maxy, minz, maxz);
  return bb;
}

export async function attach_vertices_to_reference_model(ref_bbox, phi_bbox, ref_points, vertices_to_attach, edges_to_attach, vertices_to_attach_move_directions) {
  suppress_undo_tracking();
  suppress_animate();
  const vertex_targets = new HashTable();
  const tmp_dir_hash = new HashTable();

  // part 1
  console.log("Attaching vertices part 1: Calculate initial move directions.");
  await withProgress('Attaching to vertices part 1/3', (vid) => {
    const v = uid_vertices.getItem(vid);
    const p = v.object3d.position;
    // Calculate push direction

    // We must consider two separate cases: When the vector p is already (almost) on the reference surface, and when it's far from the reference surface.
    // We need two different strategies. When it's near, we'll do a simple raycast along the vertex normal.

    // To find the normal:
    var n = new THREE.Vector3();
    var fs = [];
    var es = uil_get_edges_of_vertex(vid);
    es.forEach(function(eid){
      var twofs = uil_get_faces_of_edge(eid);
      push_nodups(twofs[0], fs);
      push_nodups(twofs[1], fs);
    });
    fs.forEach(function(fid){
      var wv_radius = new WrappedValue(0);
      var wv_flatness = new WrappedValue(0);
      var characteristic_dir_coords = new THREE.Vector3();
      var center = new THREE.Vector3();
      var n1=new THREE.Vector3();
      if (uil_is_object_visible(fid)) {
        uil_get_face_geometry(fid, center, n1, wv_radius, characteristic_dir_coords, wv_flatness);
        n.addVectors(n, n1);
      }
    });
    n.normalize();

    var move_vector = find_move_vector_from_point_to_reference(ref_points, uid_vertices.getItem(vid).object3d.position, vid, null, n, phi_bbox);

    const target = new THREE.Vector3();
    target.addVectors(p, move_vector);
    vertex_targets.setItem(vid, target);

    // Note that move_vector here has a length corresponding to the amount the vertex moved.
    vertices_to_attach_move_directions.setItem(vid, move_vector);
  })(vertices_to_attach);

  // part 2
  console.log("Attaching vertices part 2: Low-pass-filter move directions.");
  var lpf_dir_radius = 0.15; // Normalized radius wrt bbox

  await withProgress('Attaching to vertices part 2/3', (vid) => {
    const v = uid_vertices.getItem(vid);
    const p = v.object3d.position;
    const dir = new THREE.Vector3();
    dir.copy(vertices_to_attach_move_directions.getItem(vid));

    // If the distance by which vid moved is minimal, then this means that it was already almost on the target. In this case the
    // move direction may be irrelevant or misleading. So instead we consider the normal of the face at the vertex and use that
    // (we already have it in vertices_to_attach_move_directions).
    // Otherwise we do averaging with the neighbouring move directions.
    // I use a threshold of phi_bbox / 100 to decide
    if (dir.length() < phi_bbox.dimension / 100.0) {
      console.log("Main move direction too short. Using pre-calculated vertex normal direction");
      dir.normalize();
    } else {
      // First we normalize the current move direction (as we will do to all neighbouring move directions)
      dir.normalize();
      // The consider neighbours (of a significant length)
      for (const v2id of vertices_to_attach) {
        // Note: We're going quadratic within the list of vertices to attach, which is not too big,
        // and we're not doing anything very costly, so probably OK.
        var v2 = uid_vertices.getItem(v2id);
        var p2 = v2.object3d.position;
        var dist = vector3.point_point_distance(p, p2) / phi_bbox.dimension; // Normalized distance wrt bbox

        // Only consider nearby vertices.
        if (vid != v2id && dist < lpf_dir_radius) {
          // Only consider move_vectors that don't have very short length (because these could be random directions)
          var dir2 = new THREE.Vector3();
          dir2.copy(vertices_to_attach_move_directions.getItem(v2id));
          if (dir2.length > phi_bbox.dimension / 100.0) {
            dir2.normalize();
            dir2.multiplyScalar(Math.exp(dist*10.0) / Math.pow(1 + Math.exp(dist*10.0), 2.0) * 4.0); // Some bell shaped function
            dir.add(dir2);
          }
          else {
            console.log("Other move direction too short");
          }
        }
      };
      dir.normalize();
    }
    tmp_dir_hash.setItem(vid, dir);
  })(vertices_to_attach);

  // part3
  // Finally replace with filtered directions. And also replace targets with filtered versions
  await withProgress('Attaching vertices: Executing moves', vid => {
      // Replace direction
      const dir = tmp_dir_hash.getItem(vid);
      vertices_to_attach_move_directions.setItem(vid, dir);

      // Replace target
      const v = uid_vertices.getItem(vid);
      const p = v.object3d.position;
      const target = vertex_targets.getItem(vid);
      const new_target = plane_ray_intersect(target, dir, p, dir, 1.0);
      vertex_targets.setItem(vid, new_target);
  })(vertices_to_attach);

  await withProgress('Attaching to vertices part 3/3', (vid) => {
    const v = uid_vertices.getItem(vid);
    const p = v.object3d.position;
    const dir = vertices_to_attach_move_directions.getItem(vid);
    const o = raycasting_wrapper_for_vertices(
        vid,
        vertex_targets,
        vertices_to_attach_move_directions,
        p,
        dir,
        uid_ref_faces_for_attaching_surface_raynode,
        uid_ref_stl_faces_raynode,
    );
    uil_move_vertex_to_coords(vid, o.x, o.y, o.z);
  })(vertices_to_attach);

  unsuppress_animate_if_ready();
  uil_set_linearity_of_edges(edges_to_attach, true, true);
  unsuppress_undo_tracking_if_ready(false);
}

export async function attach_edge_samples_to_reference_model(
    ref_bbox,
    all_ref_points,
    edges_to_attach,
    vertices_to_attach_move_directions,
    phi_bbox,
    type,
) {
    suppress_undo_tracking();
    suppress_animate();

    var use_edges_faces = false;
    var use_vertex_move_directions = false;
    var use_closest_ref_points = false;

    if (type == 3)
        use_closest_ref_points = true;
    else if (type == 2)
        use_vertex_move_directions = true;
    else
        use_edges_faces = true;

    let msg
    if (use_edges_faces)
        msg = "Attaching edge samples using edge faces.";
    else if (use_vertex_move_directions)
        msg = "Attaching edge samples using vertex move directions.";
    else if (use_closest_ref_points)
        msg = "Attaching edge samples using closest ref points.";

    var no_raycasts = 0;
    var edge_samples_hash = new HashTable();
    var n = 0;

    let counter = edges_to_attach.length;
    let dir;

    await withProgress(msg + ' 1/2', (eid) => {
        const samples = [];

        if (use_edges_faces)
        // Calculate one direction to be used for all samples
        dir = find_direction_from_edge_sample_to_reference_based_on_faces(eid);

        for (var j = 0.04; j <= 0.96; j+=0.04) {
            const p = uil_get_point_at_edge_param(eid, j);

            if (use_vertex_move_directions)
                // Calculate a direction for each sample j based on the vertex move directions
                dir = find_direction_from_edge_sample_to_reference_based_on_vertex_move_directions(eid, j, vertices_to_attach_move_directions);

            if (use_closest_ref_points)
                // Calculate a direction for each sample j based on the closest reference_points
                dir = find_direction_from_edge_sample_to_reference_based_on_closest_reference_points(eid, j, all_ref_points, phi_bbox);

            const o = raycasting_wrapper_for_edges(p, dir, uid_ref_faces_for_attaching_surface_raynode, uid_ref_stl_faces_raynode);
            // var o = intersect_ray_with_reference_face(p, dir, uid_ref_faces_for_attaching_surface_raynode, true);
            no_raycasts++;
            //add_small_ball(o.x, o.y, o.z, 200, 0, 200, 0.4 );
            //uid_add_points_polyline_to_scene([p, dir], 200, 200, 200);
            //uid_add_vector_to_scene(p.x, p.y, p.z, dir.x, dir.y, dir.z, 1, 0,0,200);
            //console.log("Dir: " + dir.x + ", " + dir.y + ", " + dir.z + " ---> " + o.x + ", " + o.y + ", " + o.z);
            samples.push(o);
        }
        edge_samples_hash.setItem(eid, samples);
        n++;
    })(edges_to_attach);

    counter = edges_to_attach.length;
    await withProgress(msg + ' 2/2', (eid) => {
        const samples = edge_samples_hash.getItem(eid);
        const e = uid_edges.getItem(eid);
        if (e === null)
        display_message_to_user(LOG.WARN, "Unable to deform edge");
        else
        deform_edge_to_match_samples(uid_edges.getItem(eid), samples, false, false);
    })(edges_to_attach);

    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    animate();
    return no_raycasts;
}

/*

export function find_move_vector_from_phi_vertex_to_reference(ref_points, vid, phi_bbox) {
  // We must consider two separate cases: When the vector p is already (almost) on the reference surface, and when it's far from the reference surface.
  // We need two different strategies. When it's near, we'll do a simple raycast along the vertex normal.
  var v = uid_vertices.getItem(vid);
  var p = v.object3d.position;

  var move_vector = find_move_vector_from_point_to_reference(ref_points, p, vid, null, phi_bbox);
  return move_vector;
}
*/

export function find_move_vector_from_point_to_reference(ref_points, p, vid, eid, n, phi_bbox) {
  // We must consider two separate cases: When the vector p is already (almost) on the reference surface, and when it's far from the reference surface.
  // We need two different strategies. When it's near, we'll do a simple raycast along the vertex normal.
  var move_vector = new THREE.Vector3();

  // First determine if we're close. To do this, do a raycast along a normal and see if we hit the face very near.

  var int_pt = intersect_edge_ray_with_triangles(p, n, false);
  if (int_pt && vector3.point_point_distance(p, int_pt) < phi_bbox.dimension / 100.0) {
    n.multiplyScalar(phi_bbox.dimension / 300.0); //Making it small enough that it will pass the check in the next phase.
    move_vector.copy(n);
  }
  else {
    var mindist1 = 9999997;
    var mindistrefp1 = new THREE.Vector3();
    var mindist2 = 9999998;
    var mindistrefp2 = new THREE.Vector3();
    var mindist3 = 9999999;
    var mindistrefp3 = new THREE.Vector3();
    ref_points.forEach(function(tv) {
      var d = vector3.point_point_distance(tv, p);
      if ( d < mindist1) {
        mindist3 = mindist2;
        mindist2 = mindist1;
        mindist1 = d;
        mindistrefp3.x = mindistrefp2.x;
        mindistrefp3.y = mindistrefp2.y;
        mindistrefp3.z = mindistrefp2.z;
        mindistrefp2.x = mindistrefp1.x;
        mindistrefp2.y = mindistrefp1.y;
        mindistrefp2.z = mindistrefp1.z;
        mindistrefp1.x = tv.x;
        mindistrefp1.y = tv.y;
        mindistrefp1.z = tv.z;
      }
      else if (d < mindist2) {
        mindist3 = mindist2;
        mindist2 = d;
        mindistrefp3.x = mindistrefp2.x;
        mindistrefp3.y = mindistrefp2.y;
        mindistrefp3.z = mindistrefp2.z;
        mindistrefp2.x = tv.x;
        mindistrefp2.y = tv.y;
        mindistrefp2.z = tv.z;
      }
      else if (d < mindist3) {
        mindist3 = d;
        mindistrefp3.x = tv.x;
        mindistrefp3.y = tv.y;
        mindistrefp3.z = tv.z;
      }
    });
    var vd1 =  new THREE.Vector3();
    var vd2 =  new THREE.Vector3();
    var vd3 =  new THREE.Vector3();
    vd1.subVectors(mindistrefp1, p);
    vd2.subVectors(mindistrefp2, p);
    vd3.subVectors(mindistrefp3, p);
    var d1 = vd1.length();
    var d2 = vd2.length();
    var d3 = vd3.length();
    var w1 = (d2*d3) / (d1*d2 + d1*d3 + d2*d3);
    var w2 = (d1*d3) / (d1*d2 + d1*d3 + d2*d3);
    var w3 = (d1*d2) / (d1*d2 + d1*d3 + d2*d3);
    var target = new THREE.Vector3(mindistrefp1.x * w1 + mindistrefp2.x * w2 + mindistrefp3.x * w3,
                                  mindistrefp1.y * w1 + mindistrefp2.y * w2 + mindistrefp3.y * w3,
                                  mindistrefp1.z * w1 + mindistrefp2.z * w2 + mindistrefp3.z * w3);
    move_vector.subVectors(target, p);
  }
  return move_vector;
}

export function find_direction_from_edge_sample_to_reference_based_on_closest_reference_points(eid, i, ref_points, phi_bbox) {
  var vs = uil_get_vertices_of_edge(eid);
  var v0 = uil_get_point_at_edge_param(eid, 0);
  var v1 = uil_get_point_at_edge_param(eid, 1);
  if (vector3.point_point_distance(v0, uid_vertices.getItem(vs[1]).object3d.position) <
      vector3.point_point_distance(v0, uid_vertices.getItem(vs[0]).object3d.position))
    i = 1.0-i; // Flipped order of parameter, as it runs from 2nd to 1st vertex.

  var p = uil_get_point_at_edge_param(eid, i);

  // To find the normal:
  var n = new THREE.Vector3();
  var fs = [];
  var twofs = uil_get_faces_of_edge(eid);
  push_nodups(twofs[0], fs);
  push_nodups(twofs[1], fs);
  fs.forEach(function(fid){
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();
    var center = new THREE.Vector3();
    var n1=new THREE.Vector3();
    if (uil_is_object_visible(fid)) {
      uil_get_face_geometry(fid, center, n1, wv_radius, characteristic_dir_coords, wv_flatness);
      n.addVectors(n, n1);
    }
  });
  n.normalize();

  var dir = find_move_vector_from_point_to_reference(ref_points, p, null, eid, n, phi_bbox);

  return dir;
}


export function find_direction_from_edge_sample_to_reference_based_on_faces(eid) {
  var dir = new THREE.Vector3();
  var fs = uil_get_faces_of_edge(eid);
  var wv_radius = new WrappedValue(0);
  var wv_flatness = new WrappedValue(0);
  var characteristic_dir_coords = new THREE.Vector3();
  var center = new THREE.Vector3();
  var n1=new THREE.Vector3();
  var n2=new THREE.Vector3();
  if (uil_is_object_visible(fs[0]))
    uil_get_face_geometry(fs[0], center, n1, wv_radius, characteristic_dir_coords, wv_flatness);
  else
    n1 = null;
  if (uil_is_object_visible(fs[1]))
    uil_get_face_geometry(fs[1], center, n2, wv_radius, characteristic_dir_coords, wv_flatness);
  else
    n2 = null;
  if (n1 == null && n2 == null)
    display_message_to_user(LOG.WARN, "Attempting to attach edge without faces.");
  else if (n1 == null)
    dir.copy(n2).normalize();
  else if (n2 == null)
    dir.copy(n1).normalize();
  else
    dir.addVectors(n1, n2).normalize();
  return dir;
}


export function find_direction_from_edge_sample_to_reference_based_on_vertex_move_directions(eid, i, vertices_to_attach_move_directions) {
  var vs = uil_get_vertices_of_edge(eid);
  var v0 = uil_get_point_at_edge_param(eid, 0);
  var v1 = uil_get_point_at_edge_param(eid, 1);
  if (vector3.point_point_distance(v0, uid_vertices.getItem(vs[1]).object3d.position) <
      vector3.point_point_distance(v0, uid_vertices.getItem(vs[0]).object3d.position))
    i = 1.0-i; // Flipped order of parameter, as it runs from 2nd to 1st vertex.

  var d0 = vertices_to_attach_move_directions.getItem(vs[0]);
  var d1 = vertices_to_attach_move_directions.getItem(vs[1]);
  if (d0 == null || d1 == null) {
    display_message_to_user(LOG.WARN, "Problem with move directions. Were they pre-calculated?");
    return 0;
  }
  var dir = new THREE.Vector3();
  dir.addScaledVector(d0, 1.0-i);
  dir.addScaledVector(d1, i);
  dir.normalize();
  return dir;
}

export function intersect_ray_with_reference_face(p, dir, raynode, both_directions) {
  var o = new THREE.Vector3();
  var ray_intersects1 = [];
  var ray_intersects2 = [];

  dir.normalize();

  uid_raycaster.set(p, dir);
  ray_intersects1 = raycaster_intersect_objects(uid_raycaster, raynode.children);

  if (both_directions) {
    var dir2 = new THREE.Vector3();
    dir2.copy(dir);
    dir2.multiplyScalar(-1);
    uid_raycaster.set(p, dir2);
    ray_intersects2 = raycaster_intersect_objects(uid_raycaster, raynode.children);
  }

  if (ray_intersects1.length == 0 && ray_intersects2.length == 0) {
    o.copy(p);
  }
  else if (ray_intersects1.length == 0) {
    o.copy(ray_intersects2[0].point);
  }
  else if (ray_intersects2.length == 0) {
    o.copy(ray_intersects1[0].point);
  }
  else {
    if (vector3.point_point_distance(p, ray_intersects1[0].point) < vector3.point_point_distance(p, ray_intersects2[0].point) )
      o.copy(ray_intersects1[0].point);
    else
      o.copy(ray_intersects2[0].point);
  }
  return o;
}

export function collect_all_reference_points () {
  var points = [];
  uid_reference_faces.values().forEach(function(rf) {
    rf.triangle_vertices.forEach(function(v) {
        points.push(v);
    });
  });
  return points;
}

export function collect_all_reference_triangles () {
  var points = [];
  uid_reference_faces.values().forEach(function(rf) {
    rf.triangle_vertices.forEach(function(v) {
        points.push(v);
    });
  });
  return points;
}

export function enrich_reference_vertices(points, triangles) {
    var new_points = [... points];
    var new_triangles = [];

    triangles.forEach(function(t) {
      var p1 = points[t.index1];
      var p2 = points[t.index2];
      var p3 = points[t.index3];
      var pnew = new THREE.Vector3((p1.x+p2.x+p3.x)/3.0, (p1.y+p2.y+p3.y)/3.0, (p1.z+p2.z+p3.z)/3.0);
      new_points.push(pnew);
      var new_triangle1 = new Uid_face_triangle(t.index1, t.index2, new_points.length-1);
      var new_triangle2 = new Uid_face_triangle(t.index1, t.index3, new_points.length-1);
      var new_triangle3 = new Uid_face_triangle(t.index2, t.index3, new_points.length-1);
      new_triangles.push(new_triangle1);
      new_triangles.push(new_triangle2);
      new_triangles.push(new_triangle3);
    });

    // Copy new arrays in input arrays so we update them
    points.length = 0;
    triangles.length = 0;
    new_points.forEach(function(p){
      points.push(p);
    });
    new_triangles.forEach(function(t){
      triangles.push(t);
    });
    new_points = null;
    new_triangles = null;
}

export function low_pass_filter_vertices(points, rad, triplets) {
  var new_points = [];

  var points_in_radius;
  var i = 0;
  points.forEach(function(p) {

    if (i++ % 1000 == 0)
      console.log("Filtered " + i + " of " + points.length + " reference vertices");
    var newp = new THREE.Vector3();
    points_in_radius = [];

    var ar = triplets.get_relevant_points(p);

    ar.forEach(function(p1) {
      if (vector3.point_point_distance(p1, p) < rad)
        points_in_radius.push(p1);
    });
    newp.x = 0;
    newp.y = 0;
    newp.z = 0;
    points_in_radius.forEach(function(p1){
      newp.x += p1.x;
      newp.y += p1.y;
      newp.z += p1.z;
    });
    newp.x /= points_in_radius.length;
    newp.y /= points_in_radius.length;
    newp.z /= points_in_radius.length;
    new_points.push(newp);
  });

  // Copy new arrays in input arrays so we update them
  points.length = 0;
  new_points.forEach(function(p){
    points.push(p);
  });
  new_points = null;
}

export function remove_irrelevant_part_of_STL(bbox, percent_radius) {
  var new_points = [];
  var new_triangles = [];
  var c = new THREE.Vector3(bbox.centerx, bbox.centery, bbox.centerz);

  var vertices_to_attach = [];
  uid_selected_vertices.keys().forEach(function(vid){
    vertices_to_attach.push(vid);
  });

  uid_selected_edges.keys().forEach(function(eid){
    var vs = uil_get_vertices_of_edge(eid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
  });

  uid_selected_faces.keys().forEach(function(fid){
    var vs = vertices_of_face(fid);
    vs.forEach(function(vid){
      push_nodups(vid, vertices_to_attach);
    });
  });

  var old_to_new_points_hash = new HashTable();
  for (var i = all_ref_points.length-1; i >= 0; i--) {
    var close_to_vertex = false;
    vertices_to_attach.forEach(function (vid) {
      var v = uid_vertices.getItem(vid);
      if (vector3.point_point_distance(all_ref_points[i], v.object3d.position) < percent_radius * bbox.dimension)
        close_to_vertex = true;
    });
    if (close_to_vertex) {
      new_points.push(all_ref_points[i]);
      old_to_new_points_hash.setItem(i, new_points.length-1);
    }
  }

  for (var t = 0; t < all_ref_triangles.length; t++) {
    var triangle = all_ref_triangles[t];
    if (old_to_new_points_hash.getItem(triangle.index1) != null &&
        old_to_new_points_hash.getItem(triangle.index2) != null &&
        old_to_new_points_hash.getItem(triangle.index3) != null ) {
      new_triangles.push( new Uid_ref_face_triangle(old_to_new_points_hash.getItem(triangle.index1),
                                                    old_to_new_points_hash.getItem(triangle.index2),
                                                    old_to_new_points_hash.getItem(triangle.index3), null, null, null) );
    }
  }

  all_ref_triangles = new_triangles;
  all_ref_points = new_points;

  reference_object_for_attaching_surface = new Uid_ref_face( 0, new_points, new_triangles, null, true, 1, "stl"); // No onshape ID, set to 0 XXX
  var id = uid_add_reference_face(reference_object_for_attaching_surface);
  for (var i = uid_ref_faces_for_attaching_surface_raynode.children.length - 1; i >= 0; i--)
    uid_ref_faces_for_attaching_surface_raynode.remove(uid_ref_faces_for_attaching_surface_raynode.children[i]);
  uid_ref_faces_for_attaching_surface_raynode.add(uid_reference_faces.getItem(id).uid_object.object3d);
  animate();
}

export function reset_original_STL(bbox, percent_radius) {

  var rfkey_to_remove = 0;
  uid_reference_faces.keys().forEach(function(rfkey) {
    var rf = uid_reference_faces.getItem(rfkey);
    if (rf.id == reference_object_for_attaching_surface.id)
      rfkey_to_remove = rfkey;
  });
  if (rfkey_to_remove)
    uid_reference_faces.removeItem(rfkey_to_remove);

  //  reference_object_for_attaching_surface = new Uid_ref_face( 0, new_points, new_triangles, null, 1, "stl"); // No onshape ID, set to 0 XXX
//  var id = uid_add_reference_face(reference_object_for_attaching_surface);
  for (var i = uid_ref_faces_for_attaching_surface_raynode.children.length - 1; i >= 0; i--)
    uid_ref_faces_for_attaching_surface_raynode.remove(uid_ref_faces_for_attaching_surface_raynode.children[i]);
//  uid_ref_faces_for_attaching_surface_raynode.add(uid_reference_faces.getItem(id).uid_object.object3d);
  scene.remove(uid_ref_faces_for_attaching_surface_raynode);
  animate();
}

export function fill_up_STL_triangle_data(rtr) {
  var v1 = all_ref_points[rtr.index1];
  var v2 = all_ref_points[rtr.index2];
  var v3 = all_ref_points[rtr.index3];
  var p1 = new THREE.Vector3();
  var p2 = new THREE.Vector3();
  var n = new THREE.Vector3();
  var c = new THREE.Vector3();
  p1.subVectors(v1, v2);
  p2.subVectors(v1, v3);
  n.crossVectors(p1, p2).normalize();
  p1.addVectors(v1, v2);
  c.addVectors(p1, v3);
  c.multiplyScalar(1.0/3.0);
  var d = 0;
  if (vector3.point_point_distance(c, v1) > d)
    d = vector3.point_point_distance(c, v1);
  if (vector3.point_point_distance(c, v2) > d)
    d = vector3.point_point_distance(c, v2);
  if (vector3.point_point_distance(c, v3) > d)
    d = vector3.point_point_distance(c, v3);
  rtr.vertex1.copy(n);
  rtr.vertex2.copy(c);
  rtr.vertex3 = d;
}

export function raycasting_wrapper_for_vertices(vid, vertex_targets, vertices_to_attach_move_directions, p, dir, uid_ref_faces_for_attaching_surface_raynode, uid_ref_stl_faces_raynode) {
  var v = uid_vertices.getItem(vid);
  var p = v.object3d.position;

  // First we find the target for p
  var target = vertex_targets.getItem(vid);
  if (!target) {
    console.log("Unable to find target for " + vid);
    target = new THREE.Vector3();
    target.copy(p);
  }

  //log_reset_timer("B");
  var o = intersect_point_ray_with_triangles(vid, target, vertices_to_attach_move_directions, false);
  //log_time("New raycasting: ", "B");
  if (!o) {
    console.log("Failed to find intersection for " + vid);
    //log_reset_timer("B");
    var o = intersect_point_ray_with_triangles(vid, target, vertices_to_attach_move_directions, true);
    //log_time("New raycasting on all triangles: ", "B");
  }
  if (!o) {
    console.log("Falling back to traditional raycasting on reduced faces for " + vid);
    //log_reset_timer("B");
    o = intersect_ray_with_reference_face(p, dir, uid_ref_faces_for_attaching_surface_raynode, true);
    //log_time("Traditional raycasting: ", "B");
  }
  if (!o) {
    console.log("Falling back to traditional raycasting on all faces for " + vid);
    //log_reset_timer("B");
    o = intersect_ray_with_reference_face(p, dir, uid_ref_stl_faces_raynode, true);
    //log_time("Traditional raycasting: ", "B");
  }
  return o;
}

export function raycasting_wrapper_for_edges(p, dir, uid_ref_faces_for_attaching_surface_raynode, uid_ref_stl_faces_raynode) {

  //log_reset_timer("B");
  var o = intersect_edge_ray_with_triangles(p, dir, false);
  //log_time("New raycasting: ", "B");
  if (!o) {
    //console.log("Failed to find intersection for " + p);
    //log_reset_timer("B");
    var o = intersect_edge_ray_with_triangles(p, dir, true);
    //log_time("New raycasting on all triangles: ", "B");
  }
  if (!o) {
    console.log("Falling back to traditional raycasting on reduced faces for " + p);
    //log_reset_timer("B");
    o = intersect_ray_with_reference_face(p, dir, uid_ref_faces_for_attaching_surface_raynode, true);
    //log_time("Traditional raycasting: ", "B");
  }
  if (!o) {
    console.log("Falling back to traditional raycasting on all faces for " + p);
    //log_reset_timer("B");
    o = intersect_ray_with_reference_face(p, dir, uid_ref_stl_faces_raynode, true);
    //log_time("Traditional raycasting: ", "B");
  }
  return o;
}

export function intersect_point_ray_with_triangles(vid, target, vertices_to_attach_move_directions, force_on_all_triangles = false) {
  var v = uid_vertices.getItem(vid);
  var p = v.object3d.position;

  // Then we find the corresponding triangles
  var triangles;
  if (force_on_all_triangles)
    triangles = all_ref_triangles;
  else
    triangles = stl_triplets_hash.get_slot_elements_for_point(target);
  if (!triangles) {
    console.log("Unable to find triangles for " + vid);
    triangles = [];
  }

  // Then we get the direction for intersecting the triangles
  var dir = vertices_to_attach_move_directions.getItem(vid);

  // Then we do the actual intersection, that will return an array of points
  var intersection_points = [];
  triangles.forEach(function(tr) {
    var int_point = intersect_ray_with_triangle(p, dir, tr);
    if (int_point)
      intersection_points.push(int_point);
  });
  if (intersection_points.length == 0) {
    /*
    if (force_on_all_triangles)
      console.log("->>> Didn't find any intersections after forcing for " + vid);
    else
      console.log("->>> Didn't find any intersections for " + vid);
    */
    return null;
  }
  // Finally choose the point that is closest to p
  var dist = 99999;
  var new_dist;
  var closest_point;
  intersection_points.forEach(function(int_point) {
    new_dist = vector3.point_point_distance(p, int_point);
    if ( new_dist < dist) {
      dist = new_dist;
      closest_point = int_point;
    }
  });
  return closest_point;
}

export function intersect_edge_ray_with_triangles(p, dir, force_on_all_triangles = false) {
  // Then we find the corresponding triangles
  var triangles;
  if (force_on_all_triangles)
    triangles = all_ref_triangles;
  else
    triangles = stl_triplets_hash.get_slot_elements_for_point(p);
  if (!triangles) {
    console.log("Unable to find triangles for " + p);
    triangles = [];
  }

  // Then we do the actual intersection, that will return an array of points
  var intersection_points = [];
  triangles.forEach(function(tr) {
    var int_point = intersect_ray_with_triangle(p, dir, tr);
    if (int_point)
      intersection_points.push(int_point);
  });
  if (intersection_points.length == 0) {
    /*
    if (force_on_all_triangles)
      console.log("->>> Didn't find any intersections after forcing for " + p);
    else
      console.log("->>> Didn't find any intersections for " + p);
    */
    return null;
  }
  // Finally choose the point that is closest to p
  var dist = 99999;
  var new_dist;
  var closest_point;
  intersection_points.forEach(function(int_point) {
    new_dist = vector3.point_point_distance(p, int_point);
    if ( new_dist < dist) {
      dist = new_dist;
      closest_point = int_point;
    }
  });
  return closest_point;
}

export function intersect_ray_with_triangle(p, dir, tr) {

  // A reminder now that:
  // tr.vertex1 is actually the triangle normal.
  // tr.vertex2 is actually the triangle barycenter.
  // tr.vertex3 is actually a SCALAR, the radius within which the triangle lies from the barycenter.

  // If ray parallel to triangle, i.e. perpendicular to triangle normal, return null
  var phi = vector3.angle(tr.vertex1, dir);
  var two_degrees_in_rad = 0.034; // About 2 degrees
  if (maths.proximate(phi, Math.PI/2.0, two_degrees_in_rad))
    return null;

  // Intersect ray with triangle plane
  var pri = plane_ray_intersect(tr.vertex2, tr.vertex1, p, dir, 1.0);

  // Check if intersection is within triangle disk
  if (vector3.point_point_distance(pri, tr.vertex2) > tr.vertex3)
    return null;

  // Now we have to do the actual checking that the intersection point is within the triangle.
  var v1 = all_ref_points[tr.index1];
  var v2 = all_ref_points[tr.index2];
  var v3 = all_ref_points[tr.index3];
  var o1 = new THREE.Vector3();
  var o2 = new THREE.Vector3();
  var o3 = new THREE.Vector3();
  var i1 = new THREE.Vector3();
  var i2 = new THREE.Vector3();
  var i3 = new THREE.Vector3();
  var x1 = new THREE.Vector3();
  var x2 = new THREE.Vector3();
  var x3 = new THREE.Vector3();
  var n = tr.vertex1;
  o1.subVectors(v2, v1);
  o2.subVectors(v3, v2);
  o3.subVectors(v1, v3);
  i1.subVectors(pri, v1);
  i2.subVectors(pri, v2);
  i3.subVectors(pri, v3);
  x1.crossVectors(o1, i1);
  x2.crossVectors(o2, i2);
  x3.crossVectors(o3, i3);

  if ((n.dot(x1) > 0 && n.dot(x2) > 0 && n.dot(x3) > 0) || (n.dot(x1) < 0 && n.dot(x2) < 0 && n.dot(x3) < 0))
    return pri; // Intersection is within triangle - accept it.
  else
    return null; // Intersection is outside triangle - reject it.
}

export function save_reference_model_to_STL() {
  var ref_faces_to_export = [];

  // Check whether there's a defined reference_object_for_attaching_surface, otherwise use
  // all reference faces.

  if (reference_object_for_attaching_surface)
    ref_faces_to_export.push(reference_object_for_attaching_surface);
  else
    uid_reference_faces.values().forEach(function(rf) {
      ref_faces_to_export.push(rf);
    });

  var str = "solid Phi\n";

  var output_factor = 1;
  if (last_imported_stl_units == "cm")
    output_factor = 1;
  else if (last_imported_stl_units == "ft")
    output_factor = 1.0/30.479999953;
  else if (last_imported_stl_units == "in")
    output_factor = 1.0/2.54;
  else if (last_imported_stl_units == "m")
    output_factor = 0.01;
  else if (last_imported_stl_units == "mm")
    output_factor = 10;
  else if (last_imported_stl_units == "yd")
    output_factor = 1.0/91.44;

  // Factor going from current workspace units to cm
  var workspace_to_cm_factor = 1;
  if (App.state.display_units == "cm")
    workspace_to_cm_factor = 1;
  else if (App.state.display_units == "m")
    workspace_to_cm_factor = 100;
  else if (App.state.display_units == "mm")
    workspace_to_cm_factor = 0.1;
  else if (App.state.display_units == "ft")
    workspace_to_cm_factor = 30.48;
  else if (App.state.display_units == "in")
    workspace_to_cm_factor = 2.54;
  else if (App.state.display_units == "yd")
    workspace_to_cm_factor = 91.44;

  ref_faces_to_export.forEach(function(rf) {
    rf.triangles.forEach(function(tr) {
      var v1 = rf.triangle_vertices[tr.index1];
      var v2 = rf.triangle_vertices[tr.index2];
      var v3 = rf.triangle_vertices[tr.index3];

      var include_this_triangle = true;
      if (clipping_on.value) {
        if (v1.x < clipping_planes.yz.min || v2.x < clipping_planes.yz.min || v3.x < clipping_planes.yz.min ||
            v1.x > clipping_planes.yz.max || v2.x > clipping_planes.yz.max || v3.x > clipping_planes.yz.max ||
            v1.y < clipping_planes.xz.min || v2.y < clipping_planes.xz.min || v3.y < clipping_planes.xz.min ||
            v1.y > clipping_planes.xz.max || v2.y > clipping_planes.xz.max || v3.y > clipping_planes.xz.max ||
            v1.z < clipping_planes.xy.min || v2.z < clipping_planes.xy.min || v3.z < clipping_planes.xy.min ||
            v1.z > clipping_planes.xy.max || v2.z > clipping_planes.xy.max || v3.z > clipping_planes.xy.max)
          include_this_triangle = false;
      }

      if (include_this_triangle) {
        // Get actual coordinate screen values
        var v1a = get_display_vector_from_unitless_vector(v1);
        var v2a = get_display_vector_from_unitless_vector(v2);
        var v3a = get_display_vector_from_unitless_vector(v3);
        // Rescale from screen value in current display units to cm
        v1a.multiplyScalar(workspace_to_cm_factor);
        v2a.multiplyScalar(workspace_to_cm_factor);
        v3a.multiplyScalar(workspace_to_cm_factor);
        // Rescale from cm for output to file in same units as they were imported.
        v1a.multiplyScalar(output_factor);
        v2a.multiplyScalar(output_factor);
        v3a.multiplyScalar(output_factor);

        // Reminder that in vertex1 we store the normal of the triangle
        str += "facet normal " + tr.vertex1.x + " " + tr.vertex1.y + " " + tr.vertex1.z + "\n";
        str += "  outer loop\n";
        str += "    vertex " + v1a.x + " " + v1a.y  + " " + v1a.z  + "\n";
        str += "    vertex " + v2a.x + " " + v2a.y  + " " + v2a.z  + "\n";
        str += "    vertex " + v3a.x + " " + v3a.y  + " " + v3a.z  + "\n";
        str += "  endloop\n";
        str += "endfacet\n";
      }
    });
  });
  str += "endsolid Phi\n";

  var fileAsBlob = new Blob([str], { type: "text/plain" });
  var fileNameToSaveAs = App.state.current_filename + "-STL_part.stl";
  var downloadLink = document.createElement("a");
  downloadLink.download = fileNameToSaveAs;
  downloadLink.innerHTML = "Download File";
  if (window.webkitURL != null) {
    // Chrome allows the link to be clicked without actually adding it to the DOM.
    downloadLink.href = window.webkitURL.createObjectURL(fileAsBlob);
  }
  else {
    // Firefox requires the link to be added to the DOM before it can be clicked.
    downloadLink.href = window.URL.createObjectURL(fileAsBlob);
    downloadLink.onclick = destroyClickedElement;
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
  }
  downloadLink.click();
}

export function save_reduced_STL() {
    const stls = uid_reference_faces.values().filter(x => x.source === 'stl');
    switch (stls.length) {
        case 0:
            return display_message_to_user(LOG.WARN, "You need to have one STL file loaded. Aborting.");

        case 1:
            const triangles = sumBy(x => x.triangles.length)(stls);
            const str = `<p class="modal-body-text"> The STL file currently contains: ${triangles} triangles</p>`;
            $("#readSTLResizeValueCurrentTriangles").html(str);
            return show_modal("readSTLResizeValueModal");

        default:
            display_message_to_user(LOG.WARN, "Can only reduce STL file for one reference body. Aborting.");
            return;
    }
}

export async function reduce_and_save_reference_model_to_STL() {
  const stl = uid_reference_faces.values().find(x => x.source === 'stl');
  const resolution = parseInt(get_number($("#readSTLResizeValue")));
  hide_modal();
  await withSpinner('processing STL', () => {
    process_stl_to_reduce_size(stl, resolution);
  })();
}

export function process_stl_to_reduce_size(rf, resolution) {
  const bbox = uid_calculate_point_cloud_bounding_box(rf.triangle_vertices);
  const grid_size = bbox.dimension/resolution;

  // Create array with moved vertices onto grid points
  const moved_vertices = rf.triangle_vertices.map(x => vector3.copy(x));

  let c = 0;
  const moved_vertex_hash = rf.triangles.reduce((ht, tr) => {
    if (c % 10000 == 0)
        console.log('Processing:', c);
    for (const i of [tr.index1, tr.index2, tr.index3]) {
        const v = rf.triangle_vertices[i];
        const coordx = find_closest_snap_point(v.x, grid_size);
        const coordy = find_closest_snap_point(v.y, grid_size);
        const coordz = find_closest_snap_point(v.z, grid_size);
        moved_vertices[i].x = coordx;
        moved_vertices[i].y = coordy;
        moved_vertices[i].z = coordz;
        add_moved_vertex_to_hash(ht, moved_vertices[i], v);
    }
    c++;
    return ht
  }, new HashTable());

  // Remove collapsed triangles
  const moved_triangles = [];
  remove_collapsed_triangles(rf, moved_vertices, moved_triangles, moved_vertex_hash, grid_size);
}

export function get_key_from_vertex_coordinates(v) {
  return (v.x + "_" + v.y + "_" + v.z);
}

export function get_vertex_coordinates_from_key(k) {
  return(k.split('_'));
}

export function add_moved_vertex_to_hash(moved_vertex_hash, moved_v, original_v) {
  const moved_key = get_key_from_vertex_coordinates(moved_v);
  const original_key = get_key_from_vertex_coordinates(original_v);
  if (moved_vertex_hash.hasItem(moved_key)) {
    const v_hash = moved_vertex_hash.getItem(moved_key);
    if (!v_hash.hasItem(original_key))
      v_hash.setItem(original_key, original_v);
  } else {
    const v_hash = new HashTable();
    v_hash.setItem(original_key, original_v);
    moved_vertex_hash.setItem(moved_key, v_hash);
  }
}

export function remove_collapsed_triangles(rf, moved_vertices, moved_triangles, moved_vertex_hash, grid_size) {
  console.log("Removing collapsed triangles");
  var nremoved = 0;
  var scale_factor = 1000000;
  var slow_and_more_accurate = $("#readSTLResizeValueSlowCheckbox").is(':checked');
  var n_triangles = rf.triangles.length;
  var i = 0;
  var done = false;

  function remove_collapsed_triangle_set(i) {
    update_dom_text_values_and_coordinates();

    const e = grid_size / scale_factor;
    for (var k = 0; k < 1000; k++) {
      var ii = i*1000+k;
      if (ii < n_triangles) {
        var tr = rf.triangles[ii];
        var must_remove = false;
        if (
          vector3.proximate(moved_vertices[tr.index1], moved_vertices[tr.index2], e)
          || vector3.proximate(moved_vertices[tr.index1], moved_vertices[tr.index3], e)
          || vector3.proximate(moved_vertices[tr.index2], moved_vertices[tr.index3], e)
        )
          must_remove = true;
        else if (slow_and_more_accurate) {
          for (var j = 0 ; j < rf.triangles.length; j++) {
            if (j != ii) {
              var trj = rf.triangles[j];
              if (
                (
                  vector3.proximate(moved_vertices[tr.index1], moved_vertices[trj.index1], e)
                  && vector3.proximate(moved_vertices[tr.index2], moved_vertices[trj.index2], e)
                  && vector3.proximate(moved_vertices[tr.index3], moved_vertices[trj.index3], e)
                ) || (
                  vector3.proximate(moved_vertices[tr.index1], moved_vertices[trj.index1], e)
                  && vector3.proximate(moved_vertices[tr.index2], moved_vertices[trj.index3], e)
                  && vector3.proximate(moved_vertices[tr.index3], moved_vertices[trj.index2], e)
                ) || (
                  vector3.proximate(moved_vertices[tr.index1], moved_vertices[trj.index2], e)
                  && vector3.proximate(moved_vertices[tr.index2], moved_vertices[trj.index3], e)
                  && vector3.proximate(moved_vertices[tr.index3], moved_vertices[trj.index1], e)
                ) || (
                  vector3.proximate(moved_vertices[tr.index1], moved_vertices[trj.index2], e)
                  && vector3.proximate(moved_vertices[tr.index2], moved_vertices[trj.index1], e)
                  && vector3.proximate(moved_vertices[tr.index3], moved_vertices[trj.index3], e)
                ) || (
                  vector3.proximate(moved_vertices[tr.index1], moved_vertices[trj.index3], e)
                  && vector3.proximate(moved_vertices[tr.index2], moved_vertices[trj.index1], e)
                  && vector3.proximate(moved_vertices[tr.index3], moved_vertices[trj.index2], e)
                ) || (
                  vector3.proximate(moved_vertices[tr.index1], moved_vertices[trj.index3], e)
                  && vector3.proximate(moved_vertices[tr.index2], moved_vertices[trj.index2], e)
                  && vector3.proximate(moved_vertices[tr.index3], moved_vertices[trj.index1], e)
                )
              )
                must_remove = true;
            }
          }
        }
        if (must_remove)
          nremoved++;
        else
          moved_triangles.push(tr);
      }
      else {
        done = true;
      }
    }
    if (!done) {
      setTimeout(function() {remove_collapsed_triangle_set(++i);}, 5);
    }
    else {
      $( document ).trigger( "remove_collapsed_triangles_completed", [] );
    }
  }

  $( document ).one( "remove_collapsed_triangles_completed", { foo: "bar" }, function( event ) {
    $( document ).off( "remove_collapsed_triangles_completed" );
    adjust_vertex_coordinates(moved_vertices, moved_vertex_hash);
    output_reduced_stl(rf, moved_vertices, moved_triangles);
    update_dom_text_values_and_coordinates();
  });

  remove_collapsed_triangle_set(0);
}

export function adjust_vertex_coordinates(moved_vertices, moved_vertex_hash) {
  console.log("Adjusting vertex coordinates");

  for (var i = 0 ; i < moved_vertices.length; i++) {
    var v = moved_vertices[i];
    var v_hash = moved_vertex_hash.getItem(get_key_from_vertex_coordinates(v));
    if (!v_hash)
      console.log("Failed to find hash for index " + i);
    else {
      var v_average = get_average_vertex_coordinates(v_hash);
      moved_vertices[i].copy(v_average);
    }
  }
}

export function get_average_vertex_coordinates(v_hash) {
  var v_average = new THREE.Vector3(0,0,0);
  v_hash.values().forEach(function (v) {
    v_average.add(v);
  });
  v_average.multiplyScalar(1.0/v_hash.length);
  return v_average;
}


export function find_closest_snap_point(coord, grid_size) {
  // Finds the closest corner point of a grid block to a given coordinate value
  var n = Math.floor(coord/grid_size);
  var c1 = n * grid_size;
  var c2 = (n+1) * grid_size;
  var newcoord = Math.abs(c1-coord) > Math.abs(c2-coord) ? c2 : c1;
  return newcoord;
}

export function output_reduced_stl(rf, moved_vertices, moved_triangles) {

  display_message_to_user(LOG.INFO, "Saving STL with " + moved_triangles.length +
          " triangles. Original STL had " + uid_reference_faces.firstValue().triangles.length + " triangles.");

  var str = "solid Phi\n";

  moved_triangles.forEach(function(tr) {
    var v1 = moved_vertices[tr.index1];
    var v2 = moved_vertices[tr.index2];
    var v3 = moved_vertices[tr.index3];


    var output_factor = 1;
    if (last_imported_stl_units == "cm")
      output_factor = 1;
    else if (last_imported_stl_units == "ft")
      output_factor = 1.0/30.479999953;
    else if (last_imported_stl_units == "in")
      output_factor = 1.0/2.54;
    else if (last_imported_stl_units == "m")
      output_factor = 0.01;
    else if (last_imported_stl_units == "mm")
      output_factor = 10;
    else if (last_imported_stl_units == "yd")
      output_factor = 1.0/91.44;

    // Reminder that in vertex1 we store the normal of the triangle
    str += "facet normal " + tr.vertex1.x + " " + tr.vertex1.y + " " + tr.vertex1.z + "\n";
    str += "  outer loop\n";
    str += "    vertex " + (v1.x * output_factor) + " " + (v1.y * output_factor) + " " + (v1.z * output_factor) + "\n";
    str += "    vertex " + (v2.x * output_factor) + " " + (v2.y * output_factor) + " " + (v2.z * output_factor) + "\n";
    str += "    vertex " + (v3.x * output_factor) + " " + (v3.y * output_factor) + " " + (v3.z * output_factor) + "\n";
    str += "  endloop\n";
    str += "endfacet\n";
  });

  str += "endsolid Phi\n";

  var fileAsBlob = new Blob([str], { type: "text/plain" });
  var fileNameToSaveAs = App.state.current_filename + "-Reduced_to_" + moved_triangles.length + ".stl";
  var downloadLink = document.createElement("a");
  downloadLink.download = fileNameToSaveAs;
  downloadLink.innerHTML = "Download File";
  if (window.webkitURL != null) {
    // Chrome allows the link to be clicked without actually adding it to the DOM.
    downloadLink.href = window.webkitURL.createObjectURL(fileAsBlob);
  }
  else {
    // Firefox requires the link to be added to the DOM before it can be clicked.
    downloadLink.href = window.URL.createObjectURL(fileAsBlob);
    downloadLink.onclick = destroyClickedElement;
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
  }
  downloadLink.click();
}
